import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  Download as DownloadIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Helmet } from "react-helmet";
import { saveAs } from "file-saver";
import { message } from "../Constants/message.js";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import CreditsBanner from "../Components/CreditsNudge";
import YouTubeTranscriptDownloaderContent from "../Contents/YouTubeTranscriptDownloadContent.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeTranscriptDownloader({ user, login, isLoggedIn }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleUrlChange = (e) => setVideoUrl(e.target.value);

  const safeFormatTime = (seconds) => {
    if (typeof seconds !== "number" || isNaN(seconds)) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hrs, mins, secs].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const formatDuration = (duration) => {
    if (typeof duration !== "number" || isNaN(duration)) return "0s";
    return `${duration.toFixed(2)}s`;
  };

  const handleFetchTranscript = async () => {
    setError("");
    setSuccess("");
    setTranscript([]);
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => login(), 1500);
      return;
    }
    if (!videoUrl.includes("youtube.com") && !videoUrl.includes("youtu.be")) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      return;
    }
    if (credits.remaining < 1) {
      setError(
        "Insufficient credits to download YouTube transcript. Please upgrade your plan."
      );
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube-transcript-download`,
        { videoUrl },
        { headers: { "Content-Type": "application/json" } }
      );
      setTranscript(response.data.transcript);
      setSuccess(response.data.note);
      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to fetch transcript. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format) => {
    let content = "";
    if (format === "txt") {
      content = transcript.map((item) => item.text).join("\n");
    } else if (format === "srt") {
      content = transcript
        .map((item, index) => {
          const startTime = item.offset || item.start;
          const start = safeFormatTime(startTime) + ",000";
          const end = safeFormatTime(startTime + item.duration) + ",000";
          return `${index + 1}\n${start} --> ${end}\n${item.text}\n`;
        })
        .join("\n");
    }
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `transcript.${format}`);
  };

  return (
    <>
      <Helmet>
        <title>Download YouTube Transcript | Free SRT & TXT Export</title>
        <meta
          name="description"
          content="Download YouTube video captions as SRT or TXT files. View transcripts with timestamps or plain text. Fast and free. This tool help you to get transcript of youtube video"
        />
      </Helmet>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3, px: isMobile ? 2 : 6 }}>
        <ContentBox>
          <TitleText variant={isMobile ? "h4" : "h2"} align="center">
            YouTube Transcript Downloader
          </TitleText>

          <SubtitleText
            variant={isMobile ? "subtitle1" : "h6"}
            align="center"
            sx={{ mb: 2 }}
          >
            Fetch and Download YouTube Video Captions in TXT or SRT Format
          </SubtitleText>

          <TextField
            fullWidth
            label="Paste YouTube Video URL"
            variant="outlined"
            value={videoUrl}
            onChange={handleUrlChange}
            placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
            size="medium"
            sx={{ mb: 2 }}
            name="primary-textbox"
          />
          <Typography variant="body2" color="text.secondary">
            ✅ <b>1 Transcript Download = 1 Credit</b>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, textAlign: "center" }}
          >
            Note: This tool currently supports fetching YouTube transcripts only
            in English (en). Other languages are not supported at this time.
          </Typography>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="center"
            alignItems="center"
            gap={2}
            mb={3}
          >
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleFetchTranscript}
              startIcon={<DownloadIcon />}
              fullWidth={isMobile}
            >
              Fetch Transcript
            </StyledButton>
            {transcript.length > 0 && (
              <>
                <StyledButton
                  variant="outlined"
                  onClick={() => handleDownload("txt")}
                  startIcon={<NotesIcon />}
                  fullWidth={isMobile}
                >
                  Download TXT
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  onClick={() => handleDownload("srt")}
                  startIcon={<ScheduleIcon />}
                  fullWidth={isMobile}
                >
                  Download SRT
                </StyledButton>
              </>
            )}
          </Box>

          {transcript.length > 0 && (
            <Box
              sx={{
                mt: 4,
                boxShadow: 3,
                borderRadius: 4,
                overflow: "hidden",
                background: "linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  px: 3,
                  pt: 2,
                  fontWeight: 600,
                  color: "text.primary",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <NotesIcon sx={{ mr: 1, color: "primary.main" }} />
                Transcript with Timestamps
              </Typography>

              <Box sx={{ overflowX: "auto" }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table
                    sx={{
                      minWidth: 650,
                      borderCollapse: "separate",
                      borderSpacing: "0 4px",
                    }}
                  >
                    <TableHead
                      sx={{
                        background:
                          "linear-gradient(90deg, #1976d2 0%, #2196f3 100%)",
                        boxShadow: "0 2px 12px rgba(25, 118, 210, 0.2)",
                      }}
                    >
                      <TableRow>
                        <TableCell sx={{ color: "white", fontWeight: 600 }}>
                          <ScheduleIcon sx={{ fontSize: "1rem", mr: 1 }} />
                          Start Time
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: 600 }}>
                          <TimerIcon sx={{ fontSize: "1rem", mr: 1 }} />
                          Duration
                        </TableCell>
                        <TableCell sx={{ color: "white", fontWeight: 600 }}>
                          <NotesIcon sx={{ fontSize: "1rem", mr: 1 }} />
                          Text
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transcript.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                            },
                          }}
                        >
                          <TableCell>
                            <Chip
                              icon={<AccessTimeIcon />}
                              label={safeFormatTime(item.offset || item.start)}
                              size="small"
                              sx={{
                                background: "rgba(25, 118, 210, 0.1)",
                                color: "primary.dark",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatDuration(item.duration)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.text}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  background: "rgba(25, 118, 210, 0.03)",
                  borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {transcript.length} entries
                </Typography>
              </Box>
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube-transcript-downloader"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeTranscriptDownloaderContent />
      </PageContainer>
    </>
  );
}

export default YouTubeTranscriptDownloader;
